import { Component, HostListener, Inject, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialog } from "@angular/material/dialog";
//import { MatTable } from '@angular/material/table';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { TooltipPosition } from '@angular/material/tooltip';

import { Role } from '../../models/role.model';
//import { WorkflowPermissionBase } from '../../models/workflow-permission-base.model';
import { WorkflowStatePermission } from '../../models/workflow-state-permission.model';
import { WorkflowState } from '../../models/workflow-state.model';
import { WorkflowTransitionPermission } from '../../models/workflow-transition-permission.model';
import { WorkflowTransition } from '../../models/workflow-transition.model';
import { Workflow } from '../../models/site-content/workflow.model';
import { CurrentUserService } from '../../../security/current-user.service';
import { CurrentSiteService } from '../../services/current-site.service';

// Define internally used hash types.
//
// Note:  SWH added these hash types
//        so the user could save permissions
//        for multiple roles.
interface IWorkflowStatePermissionsByRoleId {
    [roleId: number]: WorkflowStatePermission[];
}
interface IWorkflowTransitionPermissionsByRoleId {
    [roleId: number]: WorkflowTransitionPermission[];
}

@Component({
    selector: 'app-configure-role-permissions',
    templateUrl: './configure-role-permissions.dialog.html',
    styleUrls: ['./configure-role-permissions.dialog.scss'],
    standalone: false
})
export class ConfigureRolePermissionsDialog implements OnInit {
    // Properties.
    private selectedRole: Role = null;
    private priorRole: Role = null;
    private workflow: Workflow = null;
    private roles: Role[] = null;

    //private newWorkflowStatePermissions: WorkflowStatePermission[] = [];
    //private newWorkflowTransitionPermissions: WorkflowTransitionPermission[] = [];
    private newWorkflowStatePermissions: IWorkflowStatePermissionsByRoleId = {};
    private newWorkflowTransitionPermissions: IWorkflowTransitionPermissionsByRoleId = {};

    private workflowStatePermissions: WorkflowStatePermission[] = null;

    private transitionPermissions: WorkflowTransitionPermission[] = null;
    private allTransitionPermissionsCanEdit: boolean = false;
    private numTransitionsChecked: number = 0;

    private readonly displayedColumns: string[] = ['workflowEntityName', 'permissions']; // for mat-table
    private readonly transitionPermissionsDisplayedColumns: string[] = ['workflowEntityName', 'permissions']; //VNEXT-306 KLW: Remove the Action column

    // Constructor.
    public constructor(
        private currentUserService: CurrentUserService,
        private currentSiteService: CurrentSiteService,
        @Inject(MAT_DIALOG_DATA) data,
        private dialogRef: MatDialogRef<ConfigureRolePermissionsDialog>
    ) {
        this.selectedRole = data.selectedRole;
        this.priorRole = this.selectedRole;
        this.workflow = data.workflow;
        this.roles = data.roles;

        this.workflowStatePermissions = this.buildWorkflowStatePermissions(null, this.selectedRole, this.workflow.states);
        this.transitionPermissions = this.buildWorkflowTransitionPermissions(null, this.selectedRole, this.workflow.transitions);

        dialogRef.disableClose = true; // Disable closing the dialogue when the user clicks on the page.
    }

    //VNEXT-329 - KLW: Create flag to represent if values have been changed
    private isDirty: boolean = false;

    //VNEXT-329 - KLW: Hook to the event before the page itself is reloaded. If there are pending changes, then
    // show a warning message
    @HostListener('window:beforeunload', ['$event'])
    public handleBeforeUnload($event: any) {
        if (this.currentUserService.userIsLoggedIn && this.isDirty) {
            $event.returnValue = "You have unsaved changes!";
        }
    }

    // Life cycle methods.
    public ngOnInit(): void {
        //this.setDisplayedColumns();
        this.isDirty = false;
    }

    //VNEXT-934: KLW - Hide and show properties when using the simple site permissions
    public get ShowOrHidePropertiesForSimplifiedPermissionsModel(): boolean {
        return this.currentSiteService.Site.useSimplifiedPermissionsModel;
    }

    public get ViewText(): string {
        let retVal: string = "View";

        if (this.currentSiteService.Site.useSimplifiedPermissionsModel) {
            retVal = "View Form";
        }

        return retVal;
    }

    public get EditText(): string {
        let retVal: string = "Edit";

        if (this.currentSiteService.Site.useSimplifiedPermissionsModel) {
            retVal = "Edit Form";
        }

        return retVal;
    }

    public get PermissionsHeader(): string {
        return this.currentSiteService.Site.useSimplifiedPermissionsModel ? "Form Permissions" : "Permissions";
    }

    // 'Accessor' methods called by my HTML.
    public get SelectedRole(): Role {
        return (this.selectedRole);
    }
    public set SelectedRole(role: Role) {
        this.selectedRole = role;
    }

    public get Roles(): Role[] {
        return (this.roles);
    }

    public get WorkflowStatePermissions(): WorkflowStatePermission[] {
        return (this.workflowStatePermissions);
    }
    public get WorkflowStatePermissionCount(): number {
        return (this.workflowStatePermissions.length);
    }

    public get TransitionPermissions(): WorkflowTransitionPermission[] {
        return (this.transitionPermissions);
    }
    /*
    public IsFirstTransitionPermission(transitionPermission: WorkflowTransitionPermission): boolean {
        let isFirst: boolean = (this.transitionPermissions[0] == transitionPermission);

        return (isFirst);
    }
    */

    public get AllTransitionPermissionsCanEdit(): boolean {
        return (this.allTransitionPermissionsCanEdit);
    }
    public set AllTransitionPermissionsCanEdit(canEditParam: boolean) {
        this.allTransitionPermissionsCanEdit = canEditParam;
    }

    public get DisplayedColumns(): string[] {
        return (this.displayedColumns);
    }
    public get TransitionPermissionsDisplayedColumns(): string[] {
        return (this.transitionPermissionsDisplayedColumns);
    }
    public get TransitionPermissionsAllCheckboxText(): string {
        let numTransitions: number = (this.transitionPermissions != null ? this.transitionPermissions.length : 0);
        let checkboxText: string = `All (${this.numTransitionsChecked} / ${numTransitions})`;

        return (checkboxText);
    }

    public get TooltipPosition(): TooltipPosition {
        let position: TooltipPosition = 'below';

        return (position);
    }
    public get OkButtonTooltip(): string {
        let tooltip: string = 'Click to apply changes made -- permissions get saved when you save the workflow itself';

        return (tooltip);
    }
    public get CancelButtonTooltip(): string {
        let tooltip: string = 'Click if you do not want to apply any changes you have made';

        return (tooltip);
    }

    // Handle control events.
    /*
    public setDisplayedColumns() {
        this.displayedColumns = ['workflowEntityName', 'permissions'];
    }
    */

    //VNEXT-306 KLW: Change the " to " text to have a dash
    public changeToDash(text: string) {
        return text.replace(" to ", " - ");
    }

    public roleChanged(unusedRole: Role) {
        this.workflowStatePermissions = this.buildWorkflowStatePermissions(this.priorRole, this.selectedRole, this.workflow.states);
        this.transitionPermissions = this.buildWorkflowTransitionPermissions(this.priorRole, this.selectedRole, this.workflow.transitions);

        this.priorRole = this.selectedRole;
    }

    public workflowStatePermissionChanged(permission: WorkflowStatePermission, permissionName: string): void {
        // If either all of the permission flags are set or none are
        // set, update the 'All' permissions checkbox value accordingly.
        if (permission.canView && permission.canEdit ) {
            permission.canDoAll = true;
        } else if ((!permission.canView) && (!permission.canEdit) ) {
            permission.canDoAll = false;
        }

        //VNEXT-329 - KLW: Set the flag to show that values have been changed
        this.isDirty = true;
    }
    public allWorkflowStatePermissionsChanged(permission: WorkflowStatePermission, eventData: MatCheckboxChange): void {
        let permissionValue: boolean = eventData.checked;

        permission.canView = permissionValue;
        permission.canEdit = permissionValue;

        //VNEXT-329 - KLW: Set the flag to show that values have been changed
        this.isDirty = true;

        return;
    }

    public workflowTransitionPermissionChanged(workflowTransitionPermissions: WorkflowTransitionPermission[], permissionName: string): void {
        let numTransitionPermission: number = 0; // Will set below.
        let numTrue: number = 0;
        let numFalse: number = 0;

        if (workflowTransitionPermissions != null) {
            numTransitionPermission = workflowTransitionPermissions.length;

            for (let iPermission: number = 0; iPermission < workflowTransitionPermissions.length; iPermission++) {
                let permission: WorkflowTransitionPermission = workflowTransitionPermissions[iPermission];

                if (permission.canEdit) {
                    numTrue++;
                } else {
                    numFalse++;
                }
            }
        }

        this.numTransitionsChecked = numTrue;

        if (numTransitionPermission > 0) {
            if (numTrue == workflowTransitionPermissions.length) {
                this.allTransitionPermissionsCanEdit = true;
            } else if (numFalse == workflowTransitionPermissions.length) {
                this.allTransitionPermissionsCanEdit = false;
            }
        }

        //VNEXT-329 - KLW: Set the flag to show that values have been changed
        this.isDirty = true;
    }
    public allWorkflowTransitionPermissionsChanged(eventData: MatCheckboxChange): void {
        let permissionValue: boolean = eventData.checked;
        //VNEXT-398 - KLW: Set variable to hold how many permissions are set
        let numTrue: number = 0;

        if (this.transitionPermissions != null) {
            for (let iPermission: number = 0; iPermission < this.transitionPermissions.length; iPermission++) {
                let permission: WorkflowTransitionPermission = this.transitionPermissions[iPermission];

                permission.canEdit = permissionValue;

                //VNEXT-398 - KLW: Increment
                numTrue++;
            }
        }

        //VNEXT-398 - KLW: Set how many permissions are true
        this.numTransitionsChecked = numTrue;

        //VNEXT-329 - KLW: Set the flag to show that values have been changed
        this.isDirty = true;

        return;
    }

    public closeDialog(save: boolean = false) {
        // Replace the permissions for any role for which we edited permissions.
        if (!save) {
            this.isDirty = false;
        }

        if (save) {
            // Save any current permissions to the hashes by role Id.
            ConfigureRolePermissionsDialog.saveWorkflowStatePermissionsToHash(this.selectedRole.id, this.workflowStatePermissions, this.newWorkflowStatePermissions);
            ConfigureRolePermissionsDialog.saveWorkflowTransitionPermissionsToHash(this.selectedRole.id, this.transitionPermissions, this.newWorkflowTransitionPermissions);

            let roleIds: string[] = Object.keys(this.newWorkflowStatePermissions);
            for (let iRole: number = 0; iRole < roleIds.length; iRole++) {
                let roleId: string = roleIds[iRole];
                let iRoleId: number = parseInt(roleId);
                let rolePermissions: WorkflowStatePermission[] = this.newWorkflowStatePermissions[iRoleId];

                for (let iPermission: number = 0; iPermission < rolePermissions.length; iPermission++) {
                    let permission: WorkflowStatePermission = rolePermissions[iPermission];

                    if (permission.hasPermissions()) {
                        //let workflowState: WorkflowState = this.workflow.states.find(ws => ws.id == permission.stateId);
                        let workflowState: WorkflowState = this.workflow.states.find(ws => ws.clientId == permission.clientId);

                        let existingStatePermission: WorkflowStatePermission =
                            (workflowState.permissions != null ? workflowState.permissions.find(wsp => wsp.roleId == iRoleId) : null);

                        if (existingStatePermission != null) {
                            workflowState.updatePermission(existingStatePermission);
                        } else {
                            let permissionClone: WorkflowStatePermission = <WorkflowStatePermission>permission.clone();

                            workflowState.addPermission(permissionClone);
                        } // if
                    } // if
                } // for
            } // for
        } // if

        if (save) {
            let roleIds: string[] = Object.keys(this.newWorkflowTransitionPermissions);
            for (let iRole: number = 0; iRole < roleIds.length; iRole++) {
                let roleId: string = roleIds[iRole];
                let iRoleId: number = parseInt(roleId);
                let rolePermissions: WorkflowTransitionPermission[] = this.newWorkflowTransitionPermissions[iRoleId];

                for (let iPermission: number = 0; iPermission < rolePermissions.length; iPermission++) {
                    let permission: WorkflowTransitionPermission = rolePermissions[iPermission];

                    if (permission.hasPermissions()) {
                        //let workflowTransition: WorkflowTransition = this.workflow.transitions.find(wt => wt.id == permission.transitionId);
                        let workflowTransition: WorkflowTransition = this.workflow.transitions.find(wt => wt.clientId == permission.clientId);

                        let existingStatePermission: WorkflowTransitionPermission =
                            (workflowTransition.permissions != null ? workflowTransition.permissions.find(wsp => wsp.roleId == iRoleId) : null);

                        if (existingStatePermission != null) {
                            workflowTransition.updatePermission(existingStatePermission);
                        } else {
                            let permissionClone: WorkflowTransitionPermission = <WorkflowTransitionPermission>permission.clone();

                            workflowTransition.addPermission(permissionClone);
                        } // if
                    } // if
                } // for
            } // for
        } // if

        //VNEXT-329 - KLW: Return back the flag if values have been changed
        this.dialogRef.close(this.isDirty);
    }

    // Private helper methods.
    private buildWorkflowStatePermissions(priorRole: Role, role: Role, workflowStates: WorkflowState[]): WorkflowStatePermission[] {
        // First, if we already have permissions
        // for a different role, save them.
        if ((priorRole != null) && (this.workflowStatePermissions != null) && (this.workflowStatePermissions.length > 0)) {
            ConfigureRolePermissionsDialog.saveWorkflowStatePermissionsToHash(priorRole.id, this.workflowStatePermissions, this.newWorkflowStatePermissions);
        }

        // Do we already have permissions for this role?
        let permissions: WorkflowStatePermission[] = this.newWorkflowStatePermissions[role.id];

        if (permissions == null) {
            // Build permissions for this role.
            permissions = [];

            if (workflowStates != null) {

                workflowStates.forEach(wfs => {
                    let p: WorkflowStatePermission =
                        (wfs.permissions != null ? wfs.permissions.filter(x => x.roleId == role.id)[0] : null);

                    if (p == null) {
                        p = new WorkflowStatePermission();

                        //this.newWorkflowStatePermissions.push(p); // store a "key" for those permissions we have to generate for the UI
                    } else {
                        this.workflowStatePermissionChanged(p, 'All'); // This will set the 'canDoAll' flag without side effects.
                    }

                    p.stateId = wfs.id;
                    p.stateName = wfs.name;
                    p.roleId = role.id;
                    p.roleName = role.name;

                    // 08-03-2021:  added the following.
                    if (wfs.clientId == 0) {
                        let errorMsg: string = `Encountered workflow state '${wfs.name}' with a clientId value of zero.`;
                        throw (errorMsg);
                    }
                    p.clientId = wfs.clientId;

                    permissions.push(p);
                });
            }
        }

        return permissions;
    }

    private buildWorkflowTransitionPermissions(priorRole: Role, role: Role, workflowTransitions: WorkflowTransition[]): WorkflowTransitionPermission[] {
        // First, if we already have permissions
        // for a different role, save them.
        if ((priorRole != null) && (this.transitionPermissions != null) && (this.transitionPermissions.length > 0)) {
            ConfigureRolePermissionsDialog.saveWorkflowTransitionPermissionsToHash(priorRole.id, this.transitionPermissions, this.newWorkflowTransitionPermissions);
        }

        // Do we already have permissions for this role?
        // Build permissions for this role.
        let permissions: WorkflowTransitionPermission[] = this.newWorkflowTransitionPermissions[role.id];

        if (permissions == null) {
            permissions = [];

            if (workflowTransitions != null) {
                workflowTransitions.forEach(wft => {
                    let p: WorkflowTransitionPermission =
                        (wft.permissions != null ? wft.permissions.filter(x => x.roleId == role.id)[0] : null);

                    if (p == null) {
                        p = new WorkflowTransitionPermission();

                        //this.newWorkflowTransitionPermissions.push(p);
                    }

                    p.transitionId = wft.id;
                    p.transitionName = wft.name;
                    p.roleId = role.id;
                    p.roleName = role.name;

                    // 08-03-2021:  added the following.
                    if (wft.clientId == 0) {
                        let errorMsg: string = `Encountered workflow transition '${wft.name}' with a clientId value of zero.`;
                        throw (errorMsg);
                    }
                    p.clientId = wft.clientId;

                    permissions.push(p);
                });
            }
        }

        // Set the all workflow transition permissions flag.
        this.workflowTransitionPermissionChanged(permissions, 'All');

        return permissions;
    }

    private static saveWorkflowStatePermissionsToHash(roleId: number, workflowStatePermissions: WorkflowStatePermission[], workflowStatePermissionsHash: IWorkflowStatePermissionsByRoleId) {
        workflowStatePermissionsHash[roleId] = workflowStatePermissions;
    }

    private static saveWorkflowTransitionPermissionsToHash(roleId: number, transitionPermissions: WorkflowTransitionPermission[], transitionPermissionsHash: IWorkflowTransitionPermissionsByRoleId) {
        transitionPermissionsHash[roleId] = transitionPermissions;
    }
}
